import { useCallback, useEffect, useState } from 'react';
import {
  CLIENT_APPS,
  isAppAvailable,
  isAppVisibleToMerchants,
  isAppVisibleToUsers,
  readClientAppOverrides,
  writeClientAppAudience,
  writeClientAppOverride,
  type ClientAppOverrides,
} from '@/lib/clientApps';

export default function ClientAppsAdminTab() {
  const [overrides, setOverrides] = useState<ClientAppOverrides>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOverrides(await readClientAppOverrides());
    } catch {
      setError('Impossible de charger les réglages des mini-applications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (slug: string, available: boolean) => {
    setBusy(slug);
    setError(null);
    try {
      const next = await writeClientAppOverride(slug, { available });
      setOverrides(next);
    } catch {
      setError('Le réglage n’a pas pu être enregistré.');
    } finally {
      setBusy(null);
    }
  };

  const changePriority = async (slug: string, priority: 'P1' | 'P2') => {
    setBusy(slug);
    setError(null);
    try {
      const next = await writeClientAppOverride(slug, { available: overrides[slug]?.available !== false, priority });
      setOverrides(next);
    } catch {
      setError('Le réglage n’a pas pu être enregistré.');
    } finally {
      setBusy(null);
    }
  };

  const toggleAudience = async (app: (typeof CLIENT_APPS)[number], audience: 'users' | 'merchants') => {
    setBusy(app.slug);
    setError(null);
    try {
      if (audience === 'users') {
        const next = await writeClientAppAudience(app.slug, {
          showToUsers: !isAppVisibleToUsers(app, overrides),
          showToMerchants: isAppVisibleToMerchants(app, overrides),
        });
        setOverrides(next);
      } else {
        const next = await writeClientAppAudience(app.slug, {
          showToUsers: isAppVisibleToUsers(app, overrides),
          showToMerchants: !isAppVisibleToMerchants(app, overrides),
        });
        setOverrides(next);
      }
    } catch {
      setError('Le réglage n’a pas pu être enregistré.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-background-100/60 border border-background-200/50 p-4">
        <h3 className="text-sm font-semibold text-foreground-900">App Store des utilisateurs</h3>
        <p className="text-xs text-foreground-600 mt-1 max-w-3xl">
          Ces mini-applications sont réservées aux comptes personnels (sans boutique). Vous pouvez les publier,
          les masquer ou changer leur priorité de mise en avant. Les données des utilisateurs restent dans leur
          espace et fonctionnent hors ligne.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {CLIENT_APPS.map((app) => {
          const available = isAppAvailable(app, overrides);
          const priority = overrides[app.slug]?.priority || app.priority;
          const toUsers = isAppVisibleToUsers(app, overrides);
          const toMerchants = isAppVisibleToMerchants(app, overrides);
          return (
            <article key={app.slug} className="bg-background-50 border border-background-200/60 rounded-lg overflow-hidden">
              <div className="flex items-start gap-3 p-4">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-background-100 flex-shrink-0">
                  <img src={app.image} alt={app.name} title={`${app.name} — mini-application Zifek`} className="w-full h-full object-cover object-top" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-foreground-900 truncate">{app.name}</h4>
                    <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium whitespace-nowrap">
                      {app.category}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${
                      available ? 'bg-secondary-100 text-secondary-900' : 'bg-background-200/70 text-foreground-600'
                    }`}>
                      {available ? 'Publiée' : 'Masquée'}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-500 mt-0.5 line-clamp-2">{app.tagline}</p>
                  <p className="text-[10px] text-foreground-400 mt-1">
                    {app.features.length} fonctionnalités · {app.offline ? 'hors ligne' : 'connexion requise'}
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap mt-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${toUsers ? 'bg-accent-100 text-accent-800' : 'bg-background-100 text-foreground-400'}`}>
                      <i className={`${toUsers ? 'ri-check-line' : 'ri-close-line'} mr-1`}></i>
                      Utilisateur normal
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${toMerchants ? 'bg-secondary-100 text-secondary-900' : 'bg-background-100 text-foreground-400'}`}>
                      <i className={`${toMerchants ? 'ri-check-line' : 'ri-close-line'} mr-1`}></i>
                      Marchand
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-4 pb-4 flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5">
                  {(['P1', 'P2'] as const).map((level) => (
                    <button
                      key={level}
                      onClick={() => changePriority(app.slug, level)}
                      disabled={busy === app.slug}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap cursor-pointer transition-colors ${
                        priority === level ? 'bg-background-50 text-foreground-900' : 'text-foreground-500'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => toggle(app.slug, !available)}
                  disabled={busy === app.slug}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 ${
                    available
                      ? 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                      : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  }`}
                >
                  {busy === app.slug ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    <i className={available ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  )}
                  {available ? 'Masquer partout' : 'Publier'}
                </button>
                <button
                  onClick={() => toggleAudience(app, 'users')}
                  disabled={busy === app.slug}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 ${
                    toUsers ? 'bg-accent-500 text-background-50 hover:bg-accent-600' : 'bg-background-100 text-foreground-500 hover:bg-background-200/70'
                  }`}
                  title="Afficher ou masquer dans l'App Store des utilisateurs normaux"
                >
                  <i className={toUsers ? 'ri-user-smile-fill' : 'ri-user-smile-line'}></i>
                  Visible utilisateur normal
                </button>
                <button
                  onClick={() => toggleAudience(app, 'merchants')}
                  disabled={busy === app.slug}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 ${
                    toMerchants ? 'bg-secondary-500 text-background-50 hover:bg-secondary-600' : 'bg-background-100 text-foreground-500 hover:bg-background-200/70'
                  }`}
                  title="Afficher ou masquer dans l'App Store des marchands"
                >
                  <i className={toMerchants ? 'ri-store-2-fill' : 'ri-store-2-line'}></i>
                  Visible marchands
                </button>
                <span className="text-[10px] text-foreground-400 ml-auto whitespace-nowrap">{app.slug}</span>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}